import { HttpInterceptor, HttpHandler, HttpRequest, HttpEvent, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { throwError, Observable, tap, catchError, NEVER, switchMap } from 'rxjs';
import { ToastService } from '../../services/toast';
import { AuthTokenService } from '../../services/auth-token.service';
import { JwtAuthService } from '../../services/jwt-auth.service';
import { ToastSeverity } from '../../models/enums';
import { ToastableResponseBody } from '../../models/service-operation-result.model';
import { bearerTokenOf, isAnonymousAuthRequest, isTokenRenewalRequest } from '../../helpers/auth-request-helper';
import { isSessionEndedFailure } from '../../helpers/token-refresh-helper';
import { REFRESHED_BEFORE_SEND } from '../../store/auth-session';

@Injectable()
export class ResponseInterceptor implements HttpInterceptor {
  private readonly handledStatus = 700;
  private readonly refreshTokenAction = 'RefreshToken';

  constructor(
    private toastService: ToastService,
    private authTokenService: AuthTokenService,
    private authService: JwtAuthService
  ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return this.handle(request, next, false);
  }

  displaySuccessToaster(method: string): void {
    switch (method) {
      case 'POST':
        this.toastService.showToaster(ToastSeverity.success, 'Added successfully');
        break;
      case 'PUT':
        this.toastService.showToaster(ToastSeverity.success, 'Modified successfully');
        break;
      case 'DELETE':
        this.toastService.showToaster(ToastSeverity.success, 'Deleted successfully');
        break;
    }
  }

  private handle(request: HttpRequest<unknown>, next: HttpHandler, retried: boolean): Observable<HttpEvent<unknown>> {
    return next.handle(request.clone()).pipe(
      tap(evt => this.announce(request, evt)),
      catchError((err: unknown) => this.recover(request, next, retried, err))
    );
  }

  private announce(request: HttpRequest<unknown>, evt: HttpEvent<unknown>): void {
    if (!(evt instanceof HttpResponse)) {
      return;
    }
    const body = evt.body as ToastableResponseBody | null;
    const notShowToaster = request.headers.get('notShowToaster');
    if (body?.isBulkAction) {
      if (body.errorsResponse?.length > 0) {
        body.errorsResponse.forEach(error => {
          this.toastService.showToaster(ToastSeverity.error, error);
        });
      }
      if (body.successResponse) {
        this.toastService.showToaster(ToastSeverity.success, body.successResponse);
      }
    } else {
      if (body?.errors?.length > 0 && notShowToaster !== 'true') {
        body.errors.forEach(error => {
          this.toastService.showToaster(ToastSeverity.error, error);
        });
      }
      if (body?.isSuccessful && notShowToaster !== 'true') {
        this.displaySuccessToaster(request.method);
      }
    }
  }

  private recover(request: HttpRequest<unknown>, next: HttpHandler, retried: boolean, err: unknown): Observable<HttpEvent<unknown>> {
    if (!(err instanceof HttpErrorResponse)) {
      return throwError(() => err);
    }
    const notShowToaster = request.headers.get('notShowToaster');
    try {
      if (this.sessionEndedInFlight(request, err)) {
        return NEVER;
      }
      if (this.shouldRefresh(request, err, retried)) {
        return this.refreshAndReplay(request, next, err);
      }
      if (err.status !== this.handledStatus && notShowToaster !== 'true') {
        this.toastService.showToaster(ToastSeverity.error, 'Something went wrong');
      }
    } catch {
      if (notShowToaster !== 'true') this.toastService.showToaster(ToastSeverity.error, 'Something went wrong');
    }
    return throwError(() => err);
  }

  private sessionEndedInFlight(request: HttpRequest<unknown>, err: HttpErrorResponse): boolean {
    return (
      this.isSessionChallenge(err) &&
      !this.isAuthEndpoint(request) &&
      bearerTokenOf(request) !== null &&
      !this.authTokenService.hasSessionToken()
    );
  }

  private shouldRefresh(request: HttpRequest<unknown>, err: HttpErrorResponse, retried: boolean): boolean {
    if (!this.isSessionChallenge(err)) {
      return false;
    }
    if (retried || request.context.get(REFRESHED_BEFORE_SEND)) {
      return false;
    }
    if (this.isAuthEndpoint(request)) {
      return false;
    }
    return this.authTokenService.hasSessionToken();
  }

  private isSessionChallenge(err: HttpErrorResponse): boolean {
    const serverAskedForRefresh = err.status === this.handledStatus && err.headers?.get('X-Auth-Action') === this.refreshTokenAction;
    return err.status === 401 || serverAskedForRefresh;
  }

  private isAuthEndpoint(request: HttpRequest<unknown>): boolean {
    return isAnonymousAuthRequest(request.url) || isTokenRenewalRequest(request.url);
  }

  private refreshAndReplay(request: HttpRequest<unknown>, next: HttpHandler, original: HttpErrorResponse): Observable<HttpEvent<unknown>> {
    return this.authService.refreshToken(bearerTokenOf(request)).pipe(
      catchError((failure: unknown) => (isSessionEndedFailure(failure) ? NEVER : throwError(() => original))),
      switchMap(token => this.handle(this.authTokenService.addTokenToRequest(request, token), next, true))
    );
  }
}
